/**
 * Блок «План квартиры» в разделе «Стены»: подложка, по которой обводят стены.
 *
 * Нет подложки — кнопка «Загрузить план»: фото или скан плана БТИ. После загрузки картинка
 * встаёт на холст, вид наезжает на неё и сразу включается масштаб — два клика по известной
 * стене и её длина. Есть подложка — масштаб заново, сдвиг, прозрачность, скрыть и убрать.
 * Убрать — правка проекта, её отменяет Ctrl+Z.
 */
import { useRef, useState } from "react";
import { useProjectStore } from "@/shared/store";
import { useUnderlayStore } from "@/shared/store/layers";
import { UIKit } from "@/shared/ui";
import { Tool, useEditorStore } from "@/widgets/editor";
import {
	readUnderlayImage,
	underlayPlacement,
	UnderlayFileError,
	UNCALIBRATED_MM_PER_PIXEL,
} from "../logic";
import { useUnderlayRecognitionStore } from "../recognitionStore";
import { commitRecognizedWalls, recognizeUnderlayWalls } from "../wallRecognition";

const BUTTON = "flex items-center justify-center gap-2 rounded-lg border px-2.5 py-2.25 text-sm";
const BUTTON_IDLE = " border-border bg-surface text-ink hover:border-slate-300 hover:bg-slate-50";
const BUTTON_ACTIVE = " border-accent bg-accent-soft font-medium text-accent";

/**
 * Кнопка «Автоматически построить стены» и блок превью/настройки чувствительности:
 * выводится в панели «Стены» сразу под кнопкой «Несущая стена».
 */
export const UnderlayAutoWallsSection = () => {
	const image = useUnderlayStore((s) => s.image);
	const calibrated = useUnderlayStore((s) => s.calibrated);
	const wallHeight = useEditorStore((s) => s.wallSettings.height);
	const recognizedWalls = useUnderlayRecognitionStore((s) => s.walls);
	const recognizing = useUnderlayRecognitionStore((s) => s.running);
	const sensitivity = useUnderlayRecognitionStore((s) => s.sensitivity);
	const includeOpenings = useUnderlayRecognitionStore((s) => s.includeOpenings);
	const [error, setError] = useState<string | null>(null);

	if (!image) return null;

	/** Запустить распознавание стен в Web Worker по текущей подложке. */
	const runRecognition = async (
		nextSensitivity = sensitivity,
		nextIncludeOpenings = includeOpenings,
	) => {
		const underlay = useUnderlayStore.getState();
		if (!underlay.image) return;
		setError(null);
		useUnderlayRecognitionStore.getState().setRunning(true);
		try {
			const found = await recognizeUnderlayWalls({
				src: underlay.image.src,
				origin: { x: underlay.x, y: underlay.y },
				mmPerPixel: underlay.mmPerPixel,
				sensitivity: nextSensitivity,
				includeOpenings: nextIncludeOpenings,
			});
			useUnderlayRecognitionStore.getState().setWalls(found);
			if (found.length === 0) {
				setError("Стены не найдены — проверьте масштаб или увеличьте чувствительность.");
			}
		} catch {
			setError("Не удалось распознать стены на плане.");
		} finally {
			useUnderlayRecognitionStore.getState().setRunning(false);
		}
	};

	const enabledCount = recognizedWalls.filter((w) => w.enabled).length;

	return (
		<div className="flex flex-col gap-2">
			{recognizedWalls.length === 0 ? (
				<>
					<UIKit.Button
						type="button"
						title={
							calibrated
								? "Найти стены на плане БТИ с учётом заданного масштаба"
								: "Сначала задайте масштаб по известной стене"
						}
						className={`${BUTTON}${BUTTON_IDLE}`}
						disabled={recognizing}
						onClick={() => {
							void runRecognition();
						}}
					>
						<UIKit.IconWall />
						{recognizing ? "Строим стены…" : "Автоматически построить стены"}
					</UIKit.Button>
					<UIKit.Button
						type="button"
						title="Включать ли автоматическое замыкание проёмов с расстановкой дверей и окон"
						className={`${BUTTON}${includeOpenings ? BUTTON_ACTIVE : BUTTON_IDLE}`}
						disabled={recognizing}
						onClick={() => {
							useUnderlayRecognitionStore.getState().setIncludeOpenings(!includeOpenings);
						}}
					>
						{includeOpenings ? "Двери и окна: вкл" : "Сплошные стены (без дверей и окон)"}
					</UIKit.Button>
					{!calibrated && (
						<UIKit.Text className="text-xs text-ink">
							Сначала укажите «Масштаб» по любой известной стене — тогда толщины стен определятся
							точно.
						</UIKit.Text>
					)}
				</>
			) : (
				<div className="flex flex-col gap-2 rounded-lg border border-accent/40 bg-accent-soft/40 p-2">
					<UIKit.Text className="text-xs leading-copy text-ink">
						Найдено стен: {enabledCount} из {recognizedWalls.length}. Кликните по лишнему отрезку на
						чертеже, чтобы отключить его.
					</UIKit.Text>
					<UIKit.Button
						type="button"
						className={`${BUTTON}${includeOpenings ? BUTTON_ACTIVE : BUTTON_IDLE}`}
						disabled={recognizing}
						onClick={() => {
							const next = !includeOpenings;
							useUnderlayRecognitionStore.getState().setIncludeOpenings(next);
							void runRecognition(sensitivity, next);
						}}
					>
						{includeOpenings ? "Двери и окна: вкл" : "Сплошные стены (без дверей и окон)"}
					</UIKit.Button>
					<UIKit.RangeNumberInput
						label="Чувствительность"
						unit="%"
						value={String(sensitivity)}
						min={15}
						max={85}
						step={5}
						onChange={(value) => {
							const next = Number(value);
							if (!Number.isFinite(next)) return;
							useUnderlayRecognitionStore.getState().setSensitivity(next);
							void runRecognition(next, includeOpenings);
						}}
					/>
					<div className="grid grid-cols-2 gap-1.5">
						<UIKit.Button
							type="button"
							className={`${BUTTON}${BUTTON_ACTIVE}`}
							disabled={recognizing || enabledCount === 0}
							onClick={() => {
								commitRecognizedWalls(recognizedWalls, wallHeight);
								useUnderlayRecognitionStore.getState().clear();
							}}
						>
							Построить ({enabledCount})
						</UIKit.Button>
						<UIKit.Button
							type="button"
							title="Убрать с холста все синие линии предпросмотра, ничего не меняя в чертеже"
							className={`${BUTTON}${BUTTON_IDLE}`}
							onClick={() => useUnderlayRecognitionStore.getState().clear()}
						>
							Отменить распознавание
						</UIKit.Button>
					</div>
				</div>
			)}
			{error && <UIKit.Text className="text-xs text-red-700">{error}</UIKit.Text>}
		</div>
	);
};

export const UnderlayPanel = () => {
	const image = useUnderlayStore((s) => s.image);
	const visible = useUnderlayStore((s) => s.visible);
	const opacity = useUnderlayStore((s) => s.opacity);
	const tool = useEditorStore((s) => s.tool);
	const setTool = useEditorStore((s) => s.setTool);
	const input = useRef<HTMLInputElement>(null);
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);

	/** Прочитать выбранный файл и положить подложку; ошибку — показать под кнопкой. */
	const load = async (file: File) => {
		setError(null);
		setLoading(true);
		useUnderlayRecognitionStore.getState().clear();
		try {
			const next = await readUnderlayImage(file);
			const graph = useProjectStore.getState().project.layers.walls;
			useProjectStore.getState().pushHistory();
			useUnderlayStore
				.getState()
				.setImage(next, underlayPlacement(next, graph), UNCALIBRATED_MM_PER_PIXEL);
			// Масштаб ещё не задан — сразу просим отметить известную стену
			useEditorStore.getState().setSelection(null);
			setTool(Tool.UnderlayScale);
		} catch (cause) {
			setError(
				cause instanceof UnderlayFileError ? cause.message : "Не получилось загрузить план.",
			);
		} finally {
			setLoading(false);
		}
	};

	const toggleTool = (target: Tool) => setTool(tool === target ? Tool.Select : target);

	return (
		<div className="flex flex-col gap-2 border-b border-border pb-2.5">
			<UIKit.Text as="span" className="text-xs font-semibold tracking-label text-ink uppercase">
				План квартиры
			</UIKit.Text>
			<UIKit.FileInput
				ref={input}
				accept="image/*"
				onFile={(file) => {
					// Обработчик синхронный — чтение файла идёт само, ошибки ловит load
					void load(file);
				}}
			/>
			{!image ? (
				<>
					<UIKit.Text className="text-xs leading-copy text-ink">
						Есть план БТИ или фото плана? Загрузите его — и обведите стены поверх, не вымеряя
						рулеткой.
					</UIKit.Text>
					<UIKit.Button
						type="button"
						className={`${BUTTON}${BUTTON_IDLE}`}
						disabled={loading}
						onClick={() => input.current?.click()}
					>
						<UIKit.IconArea />
						{loading ? "Загружаем…" : "Загрузить план"}
					</UIKit.Button>
				</>
			) : (
				<>
					<div className="grid grid-cols-2 gap-1.5">
						<UIKit.Button
							type="button"
							title="Кликните в начало и конец стены, длину которой знаете"
							className={`${BUTTON}${tool === Tool.UnderlayScale ? BUTTON_ACTIVE : BUTTON_IDLE}`}
							onClick={() => toggleTool(Tool.UnderlayScale)}
						>
							Масштаб
						</UIKit.Button>
						<UIKit.Button
							type="button"
							title="Тяните план, чтобы совместить его с чертежом"
							className={`${BUTTON}${tool === Tool.UnderlayMove ? BUTTON_ACTIVE : BUTTON_IDLE}`}
							onClick={() => toggleTool(Tool.UnderlayMove)}
						>
							Сдвинуть
						</UIKit.Button>
					</div>
					<UIKit.RangeNumberInput
						label="Яркость плана"
						unit="%"
						value={String(Math.round(opacity * 100))}
						min={10}
						max={100}
						step={5}
						onChange={(value) => {
							const percent = Number(value);
							if (Number.isFinite(percent)) useUnderlayStore.getState().setOpacity(percent / 100);
						}}
					/>
					<div className="grid grid-cols-3 gap-1.5">
						<UIKit.Button
							type="button"
							className={`${BUTTON}${BUTTON_IDLE}`}
							onClick={() => useUnderlayStore.getState().toggleVisible()}
						>
							{visible ? "Скрыть" : "Показать"}
						</UIKit.Button>
						<UIKit.Button
							type="button"
							className={`${BUTTON}${BUTTON_IDLE}`}
							disabled={loading}
							onClick={() => input.current?.click()}
						>
							Заменить
						</UIKit.Button>
						<UIKit.Button
							type="button"
							className={`${BUTTON} border-red-200 bg-surface text-red-700 hover:bg-red-50`}
							onClick={() => {
								useProjectStore.getState().pushHistory();
								useUnderlayStore.getState().clear();
								useUnderlayRecognitionStore.getState().clear();
								if (tool === Tool.UnderlayScale || tool === Tool.UnderlayMove) {
									setTool(Tool.Select);
								}
							}}
						>
							Убрать
						</UIKit.Button>
					</div>
				</>
			)}
			{error && <UIKit.Text className="text-xs text-red-700">{error}</UIKit.Text>}
		</div>
	);
};
